import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Test } from '../../interfaces/test';
import {
  ApiCollectionResponse,
  ApiSingleResponse,
} from '../../interfaces/responses/api-response';
import {
  ProfessorStudentSessionDto,
  mapSession,
} from '../professor.service';
import { SessionResultMapperService } from '../utils/session-result-mapper.service';

export interface StudentTestProgressionSummaryDto {
  studentId: string;
  totalTestsTaken: number;
  totalTestsPassed: number;
  totalTestsFailed: number;
  averageScore: number;
  averagePercentage: number;
  testsByMode?: { mode: string; count: number; averageScore: number }[];
}

@Injectable({ providedIn: 'root' })
export class AdminTestService {
  private readonly base = `${environment.BASE_URL}/admin/tests`;
  private readonly sessionMapper = inject(SessionResultMapperService);

  constructor(private http: HttpClient) {}

  allTests(): Observable<ApiCollectionResponse<Test>> {
    return this.http.get<ApiCollectionResponse<Test>>(this.base);
  }

  byDateRange(
    startDate?: string,
    endDate?: string,
  ): Observable<ApiCollectionResponse<Test>> {
    let params = new HttpParams();
    if (startDate) params = params.set('startDate', startDate);
    if (endDate) params = params.set('endDate', endDate);
    return this.http.get<ApiCollectionResponse<Test>>(
      `${this.base}/date-range`,
      { params },
    );
  }

  byMode(testMode: string): Observable<ApiCollectionResponse<Test>> {
    return this.http.get<ApiCollectionResponse<Test>>(
      `${this.base}/mode/${testMode}`,
    );
  }

  byModeAndRange(
    testMode: string,
    startDate?: string,
    endDate?: string,
  ): Observable<ApiCollectionResponse<Test>> {
    let params = new HttpParams();
    if (startDate) params = params.set('startDate', startDate);
    if (endDate) params = params.set('endDate', endDate);
    return this.http.get<ApiCollectionResponse<Test>>(
      `${this.base}/mode/${testMode}/date-range`,
      { params },
    );
  }

  /**
   * Toutes les copies de la période active, tous professeurs confondus.
   *
   * <p>Le même mapping que côté professeur, à dessein : c'est la même table de
   * sessions affichée par un autre écran. L'interpréter deux fois, c'est
   * garantir qu'un jour les deux ne diront plus la même chose.</p>
   */
  activeSessions(): Observable<ApiCollectionResponse<ProfessorStudentSessionDto>> {
    return this.mapSessions(`${this.base}/sessions/active-period`);
  }

  /** Les copies d'un élève, même période, même mapping. */
  studentActiveSessions(
    studentId: number | string,
  ): Observable<ApiCollectionResponse<ProfessorStudentSessionDto>> {
    return this.mapSessions(`${this.base}/sessions/active-period/student/${studentId}`);
  }

  private mapSessions(
    url: string,
  ): Observable<ApiCollectionResponse<ProfessorStudentSessionDto>> {
    return this.http.get<ApiCollectionResponse<any>>(url).pipe(
      map((res) => ({
        ...res,
        data: (res?.data ?? []).map((raw: any) => mapSession(raw, this.sessionMapper)),
      })),
    );
  }

  studentTests(
    studentId: number,
  ): Observable<ApiCollectionResponse<Test>> {
    return this.http.get<ApiCollectionResponse<Test>>(
      `${this.base}/student/${studentId}`,
    );
  }

  studentProgression(
    studentId: number,
    testMode?: string,
  ): Observable<ApiSingleResponse<StudentTestProgressionSummaryDto>> {
    const url = testMode
      ? `${this.base}/student/${studentId}/progression/mode/${testMode}`
      : `${this.base}/student/${studentId}/progression`;
    return this.http.get<ApiSingleResponse<StudentTestProgressionSummaryDto>>(url);
  }
}
